// Product / content-item parameter access, for custom cards and content components.
//
// Products (search + rce) carry their feed attributes as `parameters: { name, value }[]`, one
// entry per value (a multi-valued attribute repeats the name). Content items (the search API's
// additional / promo content) are typed loosely by the SDKs, so their params are read
// defensively from `unknown`.
//
// Pure and import-free, so Node's type stripping loads it (`pnpm test`).

// The product shape these helpers read — structural, so any SDK product type fits.
export type ParameterSource = { parameters: readonly { name: string; value: string }[] };

// A trimmed, non-empty string, else null. Accepts anything (for loosely typed API data).
export const nonEmptyString = (value: unknown): string | null => (typeof value === "string" && value.trim() !== "" ? value.trim() : null);

// First non-empty value of the param `name`, trimmed; null when there is none.
export const param = (product: ParameterSource, name: string): string | null => {
  for (const p of product.parameters) {
    if (p.name !== name) continue;
    const value = nonEmptyString(p.value);
    if (value !== null) return value;
  }
  return null;
};

// Every non-empty value of the param `name`, trimmed, in data order (duplicates kept).
export const params = (product: ParameterSource, name: string): string[] =>
  product.parameters.flatMap((p) => {
    if (p.name !== name) return [];
    const value = nonEmptyString(p.value);
    return value === null ? [] : [value];
  });

// The first value of the param `name` exactly as the data has it (untrimmed, may be ""); undefined
// when the param is missing. For values whose whitespace matters (e.g. a length cut on the raw text).
export const rawParam = (product: ParameterSource, name: string): string | undefined =>
  product.parameters.find((p) => p.name === name)?.value;

// What content helpers read from a content item; everything but `title` / `url` may be absent or
// malformed at runtime, whatever the SDK's declared item type says.
export type RawContentItem = {
  title: string;
  url: string;
  imageUrl?: unknown;
  parameters?: unknown;
};

// A content item param's value (first entry named `name`), trimmed; null when missing, empty or
// not a string.
export const contentParam = (item: RawContentItem, name: string): string | null => {
  if (!Array.isArray(item.parameters)) return null;
  for (const parameter of item.parameters as unknown[]) {
    if (typeof parameter === "object" && parameter !== null && (parameter as { name?: unknown }).name === name) {
      return nonEmptyString((parameter as { value?: unknown }).value);
    }
  }
  return null;
};

// A content item's image URL, or null.
export const contentImageUrl = (item: RawContentItem): string | null => nonEmptyString(item.imageUrl);
